import type { BesoinData } from '../../services/besoinsService';
import { templatePrismeKmiV1 } from './templates/prisme-kmi-v1';

export type KmiPillarScore = {
  pillarIndex: number;
  pillarTitle: string;
  /** Moyenne des réponses 1–5 du pilier (une décimale). 0 = pilier entièrement N/A. */
  averageScore: number;
  /** Nombre de réponses comptées (score > 0). Les 0 (« on ne s'en occupe pas ») sont exclus. */
  countedAnswers: number;
};

const round1 = (n: number) => Math.round(n * 10) / 10;

/**
 * Extrait un score numérique 0–5 depuis une réponse KMI.
 * - Nouvelle échelle : « 0 — … » à « 5 — … »
 * - Ancienne échelle (rétrocompat) : « 0% — … » … « 100% — … » → converti en 0–5 (proportionnel : % / 20)
 */
export function parseKmiScoreFromResponse(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  const s = value.trim();
  const mNew = s.match(/^(\d)\s*[—–-]\s*/);
  if (mNew) {
    const n = Number(mNew[1]);
    if (!Number.isFinite(n) || n < 0 || n > 5) return null;
    return n;
  }
  const mOld = s.match(/^(\d+)\s*%/);
  if (!mOld) return null;
  const pct = Number(mOld[1]);
  if (!Number.isFinite(pct) || pct < 0 || pct > 100) return null;
  return Math.round((pct / 20) * 100) / 100;
}

/** @deprecated Utiliser parseKmiScoreFromResponse */
export function parsePercentFromOptionLabel(optionLabel: unknown): number | null {
  const v = parseKmiScoreFromResponse(optionLabel);
  return v == null ? null : Math.round(v * 20);
}

function getPhaseData(besoin: any, phaseIndex1Based: number): Record<string, unknown> {
  const key = `phase${phaseIndex1Based}_data`;
  const v = besoin?.[key];
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
}

export function computeKmiPillarScores(besoin: BesoinData): KmiPillarScore[] {
  return templatePrismeKmiV1.phases.map((phase, idx) => {
    const phaseIndex = idx + 1;
    const data = getPhaseData(besoin, phaseIndex);

    const countedValues = phase.questions
      .map((q) => parseKmiScoreFromResponse(data[q.id]))
      .filter((n): n is number => typeof n === 'number' && n > 0);

    const average = countedValues.length
      ? round1(countedValues.reduce((a, b) => a + b, 0) / countedValues.length)
      : 0;

    return {
      pillarIndex: phaseIndex,
      pillarTitle: phase.title,
      averageScore: average,
      countedAnswers: countedValues.length,
    };
  });
}

export type KmiDocumentVariant = 'actuelle' | 'cible';

export function buildKmiContextMarkdown(params: {
  clientName?: string;
  besoin: BesoinData;
  variant?: KmiDocumentVariant;
}): string {
  const { clientName, besoin, variant = 'actuelle' } = params;
  const scores = computeKmiPillarScores(besoin);
  const scoresWithData = scores.filter((s) => s.countedAnswers > 0);
  const overall = scoresWithData.length
    ? round1(scoresWithData.reduce((sum, s) => sum + s.averageScore, 0) / scoresWithData.length)
    : 0;

  const lines: string[] = [];
  lines.push(
    variant === 'cible'
      ? '=== PRISME – KMI (maturité cible Futur — 6 piliers) ==='
      : '=== PRISME – KMI (diagnostic maturité actuelle — 6 piliers) ==='
  );
  lines.push(`Client: ${clientName || 'Non précisé'}`);
  lines.push(
    variant === 'cible'
      ? 'Perspective: maturité cible Futur souhaitée (état visé, pas l’état actuel).'
      : 'Perspective: maturité actuelle (état présent).'
  );
  lines.push('');
  lines.push('## Résultats (moyennes par pilier)');
  lines.push(`Échelle par question : 1 à 5 (0 = on ne s'en occupe pas, exclu du calcul).`);
  lines.push(
    `Score global (moyenne des piliers avec au moins une réponse comptée) : ${overall} / 5`
  );
  scores.forEach((s) =>
    lines.push(
      `- ${s.pillarTitle}: ${s.countedAnswers > 0 ? `${s.averageScore} / 5` : 'N/A (aucune réponse comptée)'}`
    )
  );
  lines.push('');

  templatePrismeKmiV1.phases.forEach((phase, idx) => {
    const phaseIndex = idx + 1;
    const data = getPhaseData(besoin as any, phaseIndex);
    lines.push(`## ${phase.title}`);
    lines.push(
      `Moyenne pilier : ${scores[idx]?.countedAnswers ? `${scores[idx]?.averageScore} / 5` : 'N/A (aucune réponse comptée)'}`
    );
    lines.push('');
    phase.questions.forEach((q) => {
      const v = data[q.id];
      const step = parseKmiScoreFromResponse(v);
      const vLabel = typeof v === 'string' ? v : 'Non précisé';
      lines.push(`- Q: ${q.label}`);
      lines.push(`  - Réponse: ${vLabel}${step != null ? (step === 0 ? ' (N/A, exclu du calcul)' : ` (score=${step}/5)`) : ''}`);
    });
    lines.push('');
  });

  return lines.join('\n');
}
